import ApiClient from './apiClient';
import { maybeGetRecaptchaToken } from '../utils/recaptchaV3';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone?: string;
  use_for?: string;
  language?: string;
  terms_accepted: boolean;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  email: string;
  password: string;
  password_confirmation: string;
  token: string;
}

export interface LocationResource {
  id: number;
  name: string;
  iso2?: string;
  iso3?: string;
  emoji?: string;
  state_code?: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  verified: boolean;
  phone?: string;
  use_for?: string;
  language?: string;
  company?: string;
  role?: string;
  country?: LocationResource | null;
  state?: LocationResource | null;
  city?: LocationResource | null;
  street?: string;
  house_no?: string;
  zip_code?: string;
  email_verified_at?: string;
  created_at: string;
  updated_at?: string;
  is_admin?: boolean;
  signature?: string;
  letterhead?: string;
  is_active?: boolean;
  avatar?: string | null;
  address?: string | null;
  default_shipping_id?: string | null;
  default_shipping_name?: string | null;
  default_currency_code?: string;
  
  // New location API fields
  country_id?: number | null;
  state_id?: number | null;
  city_id?: number | null;
  city_other?: string;

  // AI Shipment Agent feature flag (Phase D)
  ai_shipment_enabled?: boolean;
  terms_accepted_at?: string | null;
  terms_version?: string | null;
  privacy_version?: string | null;
}

export interface AuthResponse {
  user: User;
  token: string;
  message: string;
}

const authService = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const recaptcha_token = await maybeGetRecaptchaToken('login');
    return ApiClient.post<AuthResponse, LoginCredentials & { recaptcha_token?: string | null }>(
      '/auth/login',
      { ...credentials, recaptcha_token }
    );
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    const recaptcha_token = await maybeGetRecaptchaToken('register');
    return ApiClient.post<AuthResponse, RegisterData & { recaptcha_token?: string | null }>(
      '/auth/register',
      { ...data, recaptcha_token }
    );
  },

  logout: async (): Promise<void> => {
    try {
      await ApiClient.post('/auth/logout');
    } catch (error) {
      // Even if the API call fails, we should still clear local storage
      console.warn('Logout API call failed, but clearing local storage anyway:', error);
    } finally {
      // Always clear local storage regardless of API response
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getCurrentUser: async (): Promise<User> => {
    return ApiClient.get<User>('/auth/me');
  },

  forgotPassword: async (data: ForgotPasswordData): Promise<{ message: string }> => {
    const recaptcha_token = await maybeGetRecaptchaToken('forgot_password');
    return ApiClient.post<{ message: string }, ForgotPasswordData & { recaptcha_token?: string | null }>(
      '/auth/forgot-password',
      { ...data, recaptcha_token }
    );
  },

  resetPassword: async (data: ResetPasswordData): Promise<{ message: string }> => {
    const recaptcha_token = await maybeGetRecaptchaToken('reset_password');
    return ApiClient.post<{ message: string }, ResetPasswordData & { recaptcha_token?: string | null }>(
      '/auth/reset-password',
      { ...data, recaptcha_token }
    );
  },

  resendVerificationEmail: async (): Promise<{ message: string }> => {
    return ApiClient.post<{ message: string }>('/email/verification-notification');
  },

  verifyEmail: async (userId: string, hash: string): Promise<{ message: string }> => {
    return ApiClient.get<{ message: string }>(`/auth/email/verify/${userId}/${hash}`);
  },
};

export default authService; 
